import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VolunteerNetwork = () => {
  const navigate = useNavigate();
  
  // Real volunteer data
  const [volunteersData, setVolunteersData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load real volunteer data
  useEffect(() => {
    loadVolunteers();
  }, []);

  const loadVolunteers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/volunteers/public`);
      setVolunteersData(response.data.volunteers || []);
    } catch (error) {
      console.error('Failed to load volunteers:', error);
      setVolunteersData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [vehicleFilter, setVehicleFilter] = useState('All');
  const [minCoverage, setMinCoverage] = useState('');
  const [selectedDrawer, setSelectedDrawer] = useState(null);
  const [selectedCity, setSelectedCity] = useState('Mumbai'); // For India tab
  const [activeTab, setActiveTab] = useState('india'); // Track active country tab

  // Transport network data for statistics
  const indiaTransport = [
    { id: 1, city: "Mumbai", status: "Active", vehicles: 12, radius: 25 },
    { id: 2, city: "Delhi", status: "Active", vehicles: 10, radius: 22 },
    { id: 3, city: "Chennai", status: "Limited", vehicles: 6, radius: 18 },
    { id: 4, city: "Hyderabad", status: "Limited", vehicles: 9, radius: 20 },
    { id: 5, city: "Pune", status: "Limited", vehicles: 7, radius: 15 },
    { id: 6, city: "Bangalore", status: "Limited", vehicles: 11, radius: 25 },
    { id: 7, city: "Jaipur", status: "Limited", vehicles: 4, radius: 12 },
    { id: 8, city: "Kochi", status: "Limited", vehicles: 5, radius: 18 },
    { id: 9, city: "Ahmedabad", status: "Limited", vehicles: 6, radius: 15 },
    { id: 10, city: "Lucknow", status: "Limited", vehicles: 3, radius: 10 }
  ];

  const singaporeTransport = [
    { id: 11, district: "Orchard", status: "Active", vehicles: 3, radius: 10 },
    { id: 12, district: "Marina Bay", status: "Active", vehicles: 2, radius: 8 },
    { id: 13, district: "Chinatown", status: "Active", vehicles: 2, radius: 6 },
    { id: 14, district: "Clarke Quay", status: "Limited", vehicles: 1, radius: 5 },
    { id: 15, district: "Tiong Bahru", status: "Active", vehicles: 2, radius: 7 },
    { id: 16, district: "Sentosa", status: "Limited", vehicles: 1, radius: 6 },
    { id: 17, district: "Bukit Timah", status: "Active", vehicles: 2, radius: 8 },
    { id: 18, district: "Jurong", status: "Active", vehicles: 3, radius: 9 },
    { id: 19, district: "Novena", status: "Limited", vehicles: 1, radius: 6 },
    { id: 20, district: "Katong", status: "Active", vehicles: 2, radius: 7 }
  ];

  // Calculate transport statistics based on actual volunteer data
  const indiaStats = useMemo(() => {
    const indiaVolunteers = volunteersData.filter(v => v.country === 'IN');
    return {
      activeCities: indiaTransport.filter(city => city.status === "Active").length,
      totalVehicles: indiaVolunteers.length, // Vehicles = Volunteers
      avgRadius: Math.round(indiaTransport.reduce((sum, city) => sum + city.radius, 0) / indiaTransport.length)
    };
  }, [volunteersData]);

  const singaporeStats = useMemo(() => {
    const sgVolunteers = volunteersData.filter(v => v.country === 'SG');
    return {
      activeDistricts: singaporeTransport.filter(district => district.status === "Active").length,
      totalVehicles: sgVolunteers.length, // Vehicles = Volunteers
      avgRadius: Math.round(singaporeTransport.reduce((sum, district) => sum + district.radius, 0) / singaporeTransport.length)
    };
  }, [volunteersData]);

  // Volunteer data now loaded from backend via useEffect above
  // const volunteersData is now a state variable initialized at the top

  // Calculate volunteer counts by city
  const volunteerCounts = useMemo(() => {
    const mumbaiCount = volunteersData.filter(v => v.country === 'IN' && v.city === 'Mumbai').length;
    const delhiCount = volunteersData.filter(v => v.country === 'IN' && v.city === 'Delhi').length;
    const singaporeCount = volunteersData.filter(v => v.country === 'SG').length;
    
    return {
      mumbai: mumbaiCount,
      delhi: delhiCount,
      singapore: singaporeCount
    };
  }, [volunteersData]);

  // Filter volunteers based on current filters
  const filteredVolunteers = useMemo(() => {
    let filtered = volunteersData;

    // Country filter (based on active tab)
    if (activeTab === 'india') {
      filtered = filtered.filter(v => v.country === 'IN' && v.city === selectedCity);
    } else {
      filtered = filtered.filter(v => v.country === 'SG');
    }

    // Search filter (locality)
    if (searchTerm) {
      filtered = filtered.filter(v => 
        v.locality.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(v => v.status === statusFilter);
    }

    // Vehicle filter
    if (vehicleFilter !== 'All') {
      filtered = filtered.filter(v => v.vehicle === vehicleFilter);
    }

    // Coverage filter
    if (minCoverage) {
      filtered = filtered.filter(v => v.coverage_km >= parseInt(minCoverage));
    }

    return filtered;
  }, [volunteersData, activeTab, searchTerm, statusFilter, vehicleFilter, minCoverage, selectedCity]);

  // Calculate statistics
  const getStats = (volunteers) => {
    const active = volunteers.filter(v => v.status === 'Active').length;
    const avgCoverage = volunteers.length > 0 
      ? Math.round(volunteers.reduce((sum, v) => sum + v.coverage_km, 0) / volunteers.length)
      : 0;
    
    return {
      total: volunteers.length,
      active,
      avgCoverage
    };
  };

  // Get locality distribution
  const getLocalityStats = (volunteers) => {
    const localities = {};
    volunteers.forEach(v => {
      localities[v.locality] = (localities[v.locality] || 0) + 1;
    });
    return Object.entries(localities)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8); // Top 8 localities
  };

  // Get status distribution
  const getStatusStats = (volunteers) => {
    const statuses = { Active: 0, Busy: 0, Offline: 0 };
    volunteers.forEach(v => {
      statuses[v.status] = (statuses[v.status] || 0) + 1;
    });
    return statuses;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Busy': return 'bg-yellow-100 text-yellow-800';
      case 'Offline': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVehicleColor = (vehicle) => {
    switch (vehicle) {
      case 'Scooter': return 'bg-orange-100 text-orange-800';
      case 'Bike': return 'bg-purple-100 text-purple-800';
      case 'Car': return 'bg-red-100 text-red-800';
      case 'Van': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Page Header */}
      <section 
        className="py-20 px-4 relative bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/volunteer-network-bg.jpg')",
        }}
      >
        {/* Background overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h1 className="text-5xl font-bold text-white mb-6 drop-shadow-lg">
            Volunteer Network
          </h1>
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto drop-shadow-md">
            A snapshot of community volunteers delivering surplus food across India and Singapore.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/volunteer-signup')}
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-3 shadow-lg"
            >
              Become a Volunteer
            </Button>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Loading volunteer network...</p>
          </div>
        ) : (
          <>
        {/* Country Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
            <TabsTrigger value="india">India</TabsTrigger>
            <TabsTrigger value="singapore">Singapore</TabsTrigger>
          </TabsList>

          <TabsContent value="india">
            {/* India City Toggles */}
            <div className="flex justify-center mb-6">
              <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setSelectedCity('Mumbai')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    selectedCity === 'Mumbai' 
                      ? 'bg-green-600 text-white' 
                      : 'text-gray-600 hover:text-green-600'
                  }`}
                >
                  Mumbai ({volunteerCounts.mumbai})
                </button>
                <button
                  onClick={() => setSelectedCity('Delhi')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    selectedCity === 'Delhi' 
                      ? 'bg-green-600 text-white' 
                      : 'text-gray-600 hover:text-green-600'
                  }`}
                >
                  Delhi ({volunteerCounts.delhi})
                </button>
              </div>
            </div>

            {/* Summary Chips for India */}
            <div className="flex justify-center space-x-6 mb-8">
              {(() => {
                const cityVolunteers = volunteersData.filter(v => v.country === 'IN' && v.city === selectedCity);
                const stats = getStats(cityVolunteers);
                return (
                  <>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Total Volunteers</div>
                      <div className="text-xl font-bold text-green-600">{stats.total}</div>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Active Now</div>
                      <div className="text-xl font-bold text-green-600">{stats.active}</div>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Avg Coverage</div>
                      <div className="text-xl font-bold text-green-600">{stats.avgCoverage} km</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="singapore">
            {/* Summary Chips for Singapore */}
            <div className="flex justify-center space-x-6 mb-8">
              {(() => {
                const sgVolunteers = volunteersData.filter(v => v.country === 'SG');
                const stats = getStats(sgVolunteers);
                return (
                  <>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Total Volunteers</div>
                      <div className="text-xl font-bold text-green-600">{stats.total}</div>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Active Now</div>
                      <div className="text-xl font-bold text-green-600">{stats.active}</div>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
                      <div className="text-sm text-gray-600">Avg Coverage</div>
                      <div className="text-xl font-bold text-green-600">{stats.avgCoverage} km</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </TabsContent>
        </Tabs>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search by Locality
                </label>
                <Input
                  placeholder="e.g., Andheri, Orchard..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="All">All</option>
                  <option value="Active">Active</option>
                  <option value="Busy">Busy</option>
                  <option value="Offline">Offline</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle
                </label>
                <select
                  value={vehicleFilter}
                  onChange={(e) => setVehicleFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="All">All</option>
                  <option value="Scooter">Scooter</option>
                  <option value="Bike">Bike</option>
                  <option value="Car">Car</option>
                  <option value="Van">Van</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Coverage (km)
                </label>
                <Input
                  type="number"
                  placeholder="0"
                  value={minCoverage}
                  onChange={(e) => setMinCoverage(e.target.value)}
                />
              </div>
              
              <Button 
                onClick={() => {
                  // Filters are applied automatically via useMemo
                  // This button just provides visual feedback
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Simple Visuals */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          {/* Status Donut */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(getStatusStats(filteredVolunteers)).map(([status, count]) => (
                  <div key={status} className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${
                        status === 'Active' ? 'bg-green-500' :
                        status === 'Busy' ? 'bg-yellow-500' : 'bg-gray-400'
                      }`}></div>
                      <span className="text-sm text-gray-600">{status}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Volunteer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredVolunteers.map(volunteer => (
            <Card key={volunteer.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{volunteer.name_display}</h3>
                    <p className="text-sm text-gray-600">{volunteer.locality}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge className={getStatusColor(volunteer.status)}>
                      {volunteer.status}
                    </Badge>
                    <Badge className={getVehicleColor(volunteer.vehicle)}>
                      {volunteer.vehicle}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Coverage:</span>
                      <span className="font-medium">Up to {volunteer.coverage_km} km</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${(volunteer.coverage_km / 15) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredVolunteers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No volunteers match your current filters.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('All');
                setVehicleFilter('All');
                setMinCoverage('');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Network Statistics Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Network Statistics
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* India Stats */}
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-6">
                <div className="text-6xl mb-4">🇮🇳</div>
                <CardTitle className="text-2xl">India Network</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">📍</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{indiaStats.activeCities}</div>
                    <div className="text-sm text-gray-600">Active Cities</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">🚚</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{indiaStats.totalVehicles}</div>
                    <div className="text-sm text-gray-600">Transport Vehicles</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">📦</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{indiaStats.avgRadius}</div>
                    <div className="text-sm text-gray-600">Avg Coverage (km)</div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 font-medium">Network Reach</p>
                  <p className="text-sm text-green-700 mt-1">
                    Covering {indiaStats.activeCities} major cities with {indiaStats.totalVehicles} dedicated vehicles for food rescue deliveries
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Singapore Stats */}
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-6">
                <div className="text-6xl mb-4">🇸🇬</div>
                <CardTitle className="text-2xl">Singapore Network</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">📍</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.activeDistricts}</div>
                    <div className="text-sm text-gray-600">Active Districts</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">🚚</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.totalVehicles}</div>
                    <div className="text-sm text-gray-600">Transport Vehicles</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl">📦</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.avgRadius}</div>
                    <div className="text-sm text-gray-600">Avg Coverage (km)</div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 font-medium">Network Reach</p>
                  <p className="text-sm text-green-700 mt-1">
                    Serving {singaporeStats.activeDistricts} districts with {singaporeStats.totalVehicles} dedicated vehicles for efficient city-wide coverage
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Real-Time Network Status Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Real-Time Network Status
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">🟢</div>
                <div className="text-2xl font-bold text-green-600">{volunteersData.filter(v => v.status === 'Active').length}</div>
                <div className="text-sm text-gray-600">Active Volunteers</div>
                <div className="text-xs text-green-600 mt-1">Currently Available</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">⏱️</div>
                <div className="text-2xl font-bold text-purple-600">22</div>
                <div className="text-sm text-gray-600">Avg Response Time</div>
                <div className="text-xs text-purple-600 mt-1">Minutes</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">🎯</div>
                <div className="text-2xl font-bold text-orange-600">96%</div>
                <div className="text-sm text-gray-600">Success Rate</div>
                <div className="text-xs text-orange-600 mt-1">Last 30 Days</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Want to Join Our Volunteer Network?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Become part of Food Connect&apos;s community-driven network. Help us deliver 
            rescued food to communities and make a real impact in fighting food waste.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-3"
              data-testid="volunteer-signup-btn"
            >
              Become a Volunteer
            </Button>
          </div>
        </div>
      </section>

      {/* Volunteer Detail Drawer */}
      {selectedDrawer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Volunteer Details</h3>
              <Button variant="outline" onClick={() => setSelectedDrawer(null)}>×</Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">{selectedDrawer.name_display}</h4>
                <p className="text-gray-600">{selectedDrawer.locality}, {selectedDrawer.city}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge className={`ml-2 ${getStatusColor(selectedDrawer.status)}`}>
                    {selectedDrawer.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Vehicle:</span>
                  <Badge className={`ml-2 ${getVehicleColor(selectedDrawer.vehicle)}`}>
                    {selectedDrawer.vehicle}
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Coverage:</span>
                  <span className="text-sm font-medium">{selectedDrawer.coverage_km} km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Available:</span>
                  <span className="text-sm font-medium">{selectedDrawer.availability}</span>
                </div>
              </div>
              
              <div>
                <span className="text-sm text-gray-600 block mb-2">Typical Areas Served:</span>
                <div className="flex flex-wrap gap-1">
                  {selectedDrawer.areas_served.map((area, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerNetwork;